<template>
    <v-card class="white rounded-lg px-4 py-4 mb-5 volume-efficiency-card" outlined>
        <v-card-title class="secondary-font py-0 section-title title-row">
            <span>Volume &amp; Efficiency Diagram</span>
            <info-tip bottom max-width="560" content-class="ts-tooltip-surface diagram-tooltip-surface diagram-tooltip-surface--volume">
                <v-icon
                    small
                    class="ml-2 info-icon"
                    color="orange"
                >
                    mdi-information-outline
                </v-icon>
                <template #content>
                    <div class="tooltip-content">
                        <div>X-axis: Team Shot Attempts per 100 Poss - Opponent Shot Attempts per 100 Poss</div>
                        <div>Y-axis: Team TS% - Opponent TS%</div>
                        <div>TS% = Points / (2 x (FGA + 0.44 x FTA)) x 100</div>
                        <div>Possessions = FGA + (0.44 x FTA) - OR + TO</div>
                    </div>
                </template>
            </info-tip>
        </v-card-title>

        <v-skeleton-loader
            v-if="loading"
            type="image"
        />

        <div v-else>
            <div v-if="rows.length" class="diagram-layout">
                <div class="chart-pane">
                    <div class="chart-wrap">
                        <svg class="volume-chart" viewBox="0 0 980 620" role="img" aria-label="Volume and efficiency scatter chart">
                            <line :x1="bounds.left" :y1="bounds.bottom" :x2="bounds.right" :y2="bounds.bottom" class="axis-line" />
                            <line :x1="bounds.left" :y1="bounds.top" :x2="bounds.left" :y2="bounds.bottom" class="axis-line" />

                            <line :x1="zeroX" :y1="bounds.top" :x2="zeroX" :y2="bounds.bottom" class="center-line" />
                            <line :x1="bounds.left" :y1="zeroY" :x2="bounds.right" :y2="zeroY" class="center-line" />

                            <g v-for="tick in xTicks" :key="`x-${tick.value}`">
                                <line :x1="tick.x" :y1="bounds.bottom" :x2="tick.x" :y2="bounds.bottom + 7" class="axis-tick" />
                                <text :x="tick.x" :y="bounds.bottom + 20" class="axis-tick-label" text-anchor="middle">{{ tick.label }}</text>
                            </g>

                            <g v-for="tick in yTicks" :key="`y-${tick.value}`">
                                <line :x1="bounds.left" :y1="tick.y" :x2="bounds.left - 7" :y2="tick.y" class="axis-tick" />
                                <text :x="bounds.left - 11" :y="tick.y + 4" class="axis-tick-label" text-anchor="end">{{ tick.label }}</text>
                            </g>

                            <text :x="bounds.left + 30" :y="bounds.top + 18" class="quadrant-label">GOOD EFFICIENCY / BAD VOLUME</text>
                            <text :x="bounds.right - 248" :y="bounds.top + 18" class="quadrant-label">GOOD EFFICIENCY / GOOD VOLUME</text>
                            <text :x="bounds.left + 30" :y="bounds.bottom - 10" class="quadrant-label">BAD EFFICIENCY / BAD VOLUME</text>
                            <text :x="bounds.right - 252" :y="bounds.bottom - 10" class="quadrant-label">BAD EFFICIENCY / GOOD VOLUME</text>

                            <text :x="bounds.left - 2" :y="bounds.top - 16" class="axis-label" text-anchor="start">TS% - Opponent TS%</text>
                            <text :x="bounds.right" :y="bounds.bottom + 34" class="axis-label" text-anchor="end">Shot Attempts / 100 Poss - Opponent</text>

                            <text :x="zeroX + 8" :y="bounds.top + 34" class="average-label">AVERAGE</text>
                            <text :x="bounds.left + 8" :y="zeroY + 16" class="average-label">AVERAGE</text>

                            <g v-for="point in points" :key="point.id">
                                <image
                                    v-if="point.logo"
                                    :href="point.logo"
                                    :x="point.x - 23"
                                    :y="point.y - 23"
                                    width="46"
                                    height="46"
                                    preserveAspectRatio="xMidYMid meet"
                                />
                                <text
                                    v-else
                                    :x="point.x"
                                    :y="point.y + 4"
                                    class="fallback-team"
                                    text-anchor="middle"
                                >
                                    {{ point.shortName }}
                                </text>
                                <text
                                    :x="point.x + 28"
                                    :y="point.y - 8"
                                    :class="point.yDiff >= 0 ? 'diff-positive' : 'diff-negative'"
                                    class="diff-label"
                                >
                                    {{ point.yDiff >= 0 ? `+${format(point.yDiff)}` : format(point.yDiff) }}
                                </text>
                            </g>
                        </svg>
                    </div>
                </div>

                <div class="list-pane">
                    <div v-for="row in rows" :key="`row-${row.id}`" class="team-row">
                        <div class="team-meta">
                            <v-img
                                v-if="row.logo"
                                :src="row.logo"
                                class="team-logo"
                                contain
                            />
                            <div class="team-copy">
                                <div class="team-name secondary-font">{{ row.name }}</div>
                                <div class="team-sub secondary-font">
                                    TS%: {{ format(row.ownTs) }} / OPP: {{ format(row.oppTs) }} | SA/100: {{ format(row.ownVolume) }} / OPP: {{ format(row.oppVolume) }}
                                </div>
                            </div>
                        </div>

                        <div class="rating-visual-group">
                            <div class="rating-visual">
                                <div class="rating-label secondary-font">TS% Diff</div>
                                <div class="rating-track">
                                    <div
                                        :class="[
                                            'rating-fill',
                                            row.yDiff >= 0 ? 'rating-fill-positive' : 'rating-fill-negative'
                                        ]"
                                        :style="{ width: `${row.tsDiffWidth}%` }"
                                    />
                                </div>
                                <div :class="['rating-value', 'secondary-font', row.yDiff >= 0 ? 'diff-positive' : 'diff-negative']">
                                    {{ row.yDiff >= 0 ? `+${format(row.yDiff)}` : format(row.yDiff) }}
                                </div>
                            </div>

                            <div class="rating-visual">
                                <div class="rating-label secondary-font">Vol Diff</div>
                                <div class="rating-track">
                                    <div
                                        :class="[
                                            'rating-fill',
                                            row.xDiff >= 0 ? 'rating-fill-positive' : 'rating-fill-negative'
                                        ]"
                                        :style="{ width: `${row.volDiffWidth}%` }"
                                    />
                                </div>
                                <div :class="['rating-value', 'secondary-font', row.xDiff >= 0 ? 'diff-positive' : 'diff-negative']">
                                    {{ row.xDiff >= 0 ? `+${format(row.xDiff)}` : format(row.xDiff) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="secondary-font empty-state">
                No team stats available for the selected filters.
            </div>
        </div>
    </v-card>
</template>

<script>
    export default {
        name: 'VolumeEfficiencyDiagram',
        props: {
            teams: {
                type: Array,
                default: () => []
            },
            loading: {
                type: Boolean,
                default: false
            },
            assetsUrl: {
                type: String,
                default: 'https://storage.googleapis.com'
            }
        },
        computed: {
            bounds() {
                return {
                    left: 58,
                    right: 900,
                    top: 36,
                    bottom: 560
                }
            },
            perTeamPerMatch() {
                const byTeam = {}

                this.teams.forEach((team, teamIndex) => {
                    const teamKey = this.getTeamKey(team, teamIndex)
                    byTeam[teamKey] = {}
                    const players = Array.isArray(team && team.players) ? team.players : []

                    players.forEach(player => {
                        const stats = Array.isArray(player && player.stats) ? player.stats : []
                        stats.forEach(stat => {
                            if (stat && stat.cup) return
                            const matchId = stat && stat.matchId
                            if (matchId === null || matchId === undefined) return

                            if (!byTeam[teamKey][matchId]) {
                                byTeam[teamKey][matchId] = {
                                    points: 0,
                                    fga: 0,
                                    fta: 0,
                                    offensiveRebounds: 0,
                                    turnovers: 0
                                }
                            }

                            byTeam[teamKey][matchId].points += Number(stat.points) || 0
                            byTeam[teamKey][matchId].fga += (Number(stat.twoPointAttempts) || 0) + (Number(stat.threePointAttempts) || 0)
                            byTeam[teamKey][matchId].fta += Number(stat.freeThrowAttempts) || 0
                            byTeam[teamKey][matchId].offensiveRebounds += Number(stat.offensiveRebounds) || 0
                            byTeam[teamKey][matchId].turnovers += Number(stat.turnovers) || 0
                        })
                    })
                })

                return byTeam
            },
            perMatch() {
                const byMatch = {}

                Object.entries(this.perTeamPerMatch).forEach(([teamKey, matches]) => {
                    Object.entries(matches).forEach(([matchId, totals]) => {
                        if (!byMatch[matchId]) byMatch[matchId] = {}
                        byMatch[matchId][teamKey] = totals
                    })
                })

                return byMatch
            },
            rows() {
                const baseRows = this.teams.map((team, teamIndex) => {
                    const teamKey = this.getTeamKey(team, teamIndex)
                    const ownTotals = this.getTotals(team)

                    const oppTotals = {
                        points: 0,
                        fga: 0,
                        fta: 0,
                        offensiveRebounds: 0,
                        turnovers: 0
                    }

                    const ownMatches = this.perTeamPerMatch[teamKey] || {}
                    Object.keys(ownMatches).forEach(matchId => {
                        const participants = this.perMatch[matchId] || {}
                        Object.entries(participants).forEach(([otherKey, totals]) => {
                            if (otherKey === teamKey) return
                            oppTotals.points += Number(totals.points) || 0
                            oppTotals.fga += Number(totals.fga) || 0
                            oppTotals.fta += Number(totals.fta) || 0
                            oppTotals.offensiveRebounds += Number(totals.offensiveRebounds) || 0
                            oppTotals.turnovers += Number(totals.turnovers) || 0
                        })
                    })

                    const ownPoss = this.possessions(ownTotals)
                    const oppPoss = this.possessions(oppTotals)
                    const ownTs = this.tsPct(ownTotals.points, ownTotals.fga, ownTotals.fta)
                    const oppTs = this.tsPct(oppTotals.points, oppTotals.fga, oppTotals.fta)
                    const ownVolume = this.ratePer100(ownTotals.fga, ownPoss)
                    const oppVolume = this.ratePer100(oppTotals.fga, oppPoss)

                    return {
                        id: team.id || team._id || team.name,
                        name: team.name || 'Team',
                        logo: this.getLogo(team),
                        ownTs,
                        oppTs,
                        ownVolume,
                        oppVolume,
                        xDiff: ownVolume - oppVolume,
                        yDiff: ownTs - oppTs
                    }
                }).sort((a, b) => b.yDiff - a.yDiff)

                const maxAbsTsDiff = Math.max(...baseRows.map(row => Math.abs(row.yDiff)), 1)
                const maxAbsVolDiff = Math.max(...baseRows.map(row => Math.abs(row.xDiff)), 1)

                return baseRows.map(row => ({
                    ...row,
                    tsDiffWidth: Math.max(8, (Math.abs(row.yDiff) / maxAbsTsDiff) * 100),
                    volDiffWidth: Math.max(8, (Math.abs(row.xDiff) / maxAbsVolDiff) * 100)
                }))
            },
            xMin() {
                if (!this.rows.length) return -6
                const rawMin = Math.min(...this.rows.map(row => row.xDiff))
                const rawMax = Math.max(...this.rows.map(row => row.xDiff))
                const pad = Math.max(1.2, (rawMax - rawMin) * 0.2)
                return Math.min(rawMin, 0) - pad
            },
            xMax() {
                if (!this.rows.length) return 6
                const rawMin = Math.min(...this.rows.map(row => row.xDiff))
                const rawMax = Math.max(...this.rows.map(row => row.xDiff))
                const pad = Math.max(1.2, (rawMax - rawMin) * 0.2)
                return Math.max(rawMax, 0) + pad
            },
            yMin() {
                if (!this.rows.length) return -6
                const rawMin = Math.min(...this.rows.map(row => row.yDiff))
                const rawMax = Math.max(...this.rows.map(row => row.yDiff))
                const pad = Math.max(1.2, (rawMax - rawMin) * 0.2)
                return Math.min(rawMin, 0) - pad
            },
            yMax() {
                if (!this.rows.length) return 6
                const rawMin = Math.min(...this.rows.map(row => row.yDiff))
                const rawMax = Math.max(...this.rows.map(row => row.yDiff))
                const pad = Math.max(1.2, (rawMax - rawMin) * 0.2)
                return Math.max(rawMax, 0) + pad
            },
            zeroX() {
                return this.xToCoord(0)
            },
            zeroY() {
                return this.yToCoord(0)
            },
            points() {
                return this.rows.map(row => ({
                    ...row,
                    x: this.clamp(this.xToCoord(row.xDiff), this.bounds.left + 20, this.bounds.right - 20),
                    y: this.clamp(this.yToCoord(row.yDiff), this.bounds.top + 22, this.bounds.bottom - 22),
                    shortName: String(row.name || 'T').slice(0, 2).toUpperCase()
                }))
            },
            xTicks() {
                const steps = 4
                const span = this.xMax - this.xMin
                return Array.from({ length: steps + 1 }, (_, i) => {
                    const value = this.xMin + (span * (i / steps))
                    return {
                        value,
                        x: this.xToCoord(value),
                        label: this.format(value)
                    }
                })
            },
            yTicks() {
                const steps = 4
                const span = this.yMax - this.yMin
                return Array.from({ length: steps + 1 }, (_, i) => {
                    const value = this.yMin + (span * (i / steps))
                    return {
                        value,
                        y: this.yToCoord(value),
                        label: this.format(value)
                    }
                })
            }
        },
        methods: {
            getTeamKey(team = {}, teamIndex = 0) {
                return String(team.id || team._id || team.name || `team-${teamIndex}`)
            },
            getLogo(team = {}) {
                const raw = team.logo || team.image || null
                if (!raw) return null
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            getTotals(team = {}) {
                if (team.cum) {
                    return {
                        points: Number(team.cum.points) || 0,
                        fga: (Number(team.cum.twoPointAttempts) || 0) + (Number(team.cum.threePointAttempts) || 0),
                        fta: Number(team.cum.freeThrowAttempts) || 0,
                        offensiveRebounds: Number(team.cum.offensiveRebounds) || 0,
                        turnovers: Number(team.cum.turnovers) || 0
                    }
                }

                const totals = {
                    points: 0,
                    fga: 0,
                    fta: 0,
                    offensiveRebounds: 0,
                    turnovers: 0
                }

                const stats = Array.isArray(team.stats) ? team.stats : []
                stats.forEach(stat => {
                    totals.points += Number(stat.points) || 0
                    totals.fga += (Number(stat.twoPointAttempts) || 0) + (Number(stat.threePointAttempts) || 0)
                    totals.fta += Number(stat.freeThrowAttempts) || 0
                    totals.offensiveRebounds += Number(stat.offensiveRebounds) || 0
                    totals.turnovers += Number(stat.turnovers) || 0
                })

                return totals
            },
            possessions(totals = {}) {
                const fga = Number(totals.fga) || 0
                const fta = Number(totals.fta) || 0
                const offensiveRebounds = Number(totals.offensiveRebounds) || 0
                const turnovers = Number(totals.turnovers) || 0
                return Math.max(0, fga + (0.44 * fta) - offensiveRebounds + turnovers)
            },
            tsPct(points = 0, fga = 0, fta = 0) {
                const denominator = 2 * (Number(fga) + (0.44 * Number(fta)))
                if (denominator <= 0) return 0
                return (Number(points) / denominator) * 100
            },
            ratePer100(value = 0, possessions = 0) {
                if (possessions <= 0) return 0
                return (Number(value) / possessions) * 100
            },
            xToCoord(value) {
                const span = Math.max(0.1, this.xMax - this.xMin)
                const normalized = (value - this.xMin) / span
                return this.bounds.left + (normalized * (this.bounds.right - this.bounds.left))
            },
            yToCoord(value) {
                const span = Math.max(0.1, this.yMax - this.yMin)
                const normalized = (value - this.yMin) / span
                return this.bounds.bottom - (normalized * (this.bounds.bottom - this.bounds.top))
            },
            clamp(value, min, max) {
                return Math.min(max, Math.max(min, value))
            },
            format(value) {
                return Number(value || 0).toFixed(1)
            }
        }
    }
</script>

<style scoped>
.section-title {
    border-bottom: 1px solid #d7dbe3;
    margin-bottom: 8px;
    width: 100%;
}

.title-row {
    display: flex;
    align-items: center;
}

.info-icon {
    cursor: pointer;
}

.tooltip-content {
    font-size: 13px;
    line-height: 1.4;
    width: 100%;
}

.diagram-layout {
    display: flex;
    gap: 14px;
    align-items: stretch;
}

.chart-pane,
.list-pane {
    flex: 1;
    min-width: 0;
}

.chart-pane {
    display: flex;
    align-items: center;
}

.chart-wrap {
    width: 100%;
}

.volume-chart {
    width: 100%;
    height: auto;
    display: block;
    aspect-ratio: 980 / 620;
}

.axis-line {
    stroke: #2f3f53;
    stroke-width: 2.2;
}

.center-line {
    stroke: #7a8899;
    stroke-width: 1.5;
}

.axis-tick {
    stroke: #4c5b6f;
    stroke-width: 1.3;
}

.axis-tick-label {
    fill: #55657a;
    font-size: 11px;
    font-weight: 700;
}

.axis-label {
    fill: #2f3f53;
    font-size: 14px;
    font-weight: 700;
}

.average-label {
    fill: #616e7f;
    font-size: 12px;
    font-weight: 700;
}

.quadrant-label {
    fill: #5e6c7d;
    font-size: 11px;
    font-weight: 700;
}

.fallback-team {
    fill: #1b273b;
    font-size: 12px;
    font-weight: 800;
}

.diff-label {
    font-size: 15px;
    font-weight: 900;
}

.diff-positive {
    fill: #1f9d47;
    color: #1f9d47;
}

.diff-negative {
    fill: #da3e3e;
    color: #da3e3e;
}

.team-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #eceff5;
}

.team-row:last-child {
    border-bottom: 0;
}

.list-pane {
    max-height: 620px;
    overflow: auto;
    padding-right: 4px;
}

.team-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.team-logo {
    width: 52px;
    height: 52px;
    border-radius: 0;
    background: #fff;
}

.team-copy {
    text-align: left;
}

.rating-visual-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    max-width: 440px;
}

.rating-visual {
    display: flex;
    align-items: center;
    gap: 10px;
}

.rating-label {
    min-width: 64px;
    text-align: right;
    font-size: 12px;
    font-weight: 800;
    color: #4b5a6f;
}

.rating-track {
    width: 100%;
    height: 12px;
    border-radius: 999px;
    background: #eceff4;
    overflow: hidden;
}

.rating-fill {
    height: 100%;
    border-radius: 999px;
}

.rating-fill-positive {
    background: linear-gradient(90deg, #1f9d47 0%, #2fb157 70%, #6bcf7f 100%);
}

.rating-fill-negative {
    background: linear-gradient(90deg, #b42323 0%, #d33c3c 70%, #f07171 100%);
}

.rating-value {
    min-width: 54px;
    text-align: right;
    font-size: 16px;
    font-weight: 900;
}

.team-name {
    font-size: 16px;
    font-weight: 800;
    color: #182a3d;
}

.team-details,
.team-sub {
    font-size: 12px;
    font-weight: 600;
    color: #5d6f83;
}

.empty-state {
    color: #5d6f83;
    padding: 8px 0;
}

@media only screen and (max-width: 960px) {
    .diagram-layout {
        flex-direction: column;
    }

    .list-pane {
        max-height: none;
        overflow: visible;
        padding-right: 0;
    }

    .team-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .rating-visual-group {
        width: 100%;
        max-width: none;
    }
}

::v-deep .v-tooltip__content.ts-tooltip-surface {
    background: #fff !important;
    opacity: 1 !important;
    color: #111;
    border: 1px solid #d7dbe3;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

::v-deep .v-tooltip__content.diagram-tooltip-surface {
    white-space: normal !important;
    overflow-wrap: break-word;
    word-break: normal;
}

::v-deep .v-tooltip__content.diagram-tooltip-surface--volume {
    width: min(560px, calc(100vw - 32px)) !important;
    max-width: min(560px, calc(100vw - 32px)) !important;
}
</style>
